import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fmtNumber } from '@/shared/lib/format'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import type { FieldSpec, PreviewResponse, TargetGroupOption } from '@/shared/api/types'
import { composeDefinition, previewTargeting, targetingFields, targetingGroups } from './api'
import { fieldLabel, operatorLabel, valueLabel } from './vocab'

/**
 * The targeting rule builder.
 *
 * ## It is driven by the backend's whitelist, and there is no second copy
 *
 * Every field, every operator and every allowed value comes from
 * `GET /programs/targeting/fields` at runtime. This file contains no list of
 * segment fields — only LABELS for the ones it recognises, with a humanised
 * fallback for anything newer.
 *
 * That is not fastidiousness. PollSphere's builder offered fields its own
 * backend did not evaluate, so a campaign could compose a rule, watch the
 * preview count move, publish it, and enrol nobody. A builder that cannot drift
 * from the evaluator is the only builder worth shipping.
 *
 * ## A preset says what it needs
 *
 * Each chip carries its live matching count AND, when it depends on voter tags,
 * how many voters actually carry each tag. A preset whose tags nobody has
 * applied renders "requires tag BODA — 0 voters tagged" rather than a bare 0,
 * because a zero that looks like a filter result is indistinguishable from a
 * zero that means "we never collected this".
 *
 * ## An empty group matches everybody
 *
 * `{op: AND, children: []}` compiles to a conjunction over zero restrictions,
 * which is `true`. The server refuses it; so does this builder, before the
 * request is made, because a preview that quietly returns the whole register is
 * a data-export hole rather than an empty result.
 */

export interface Leaf { field: string; operator: string; value: unknown }
export interface Branch { op: 'AND' | 'OR'; children: RuleNode[] }
export type RuleNode = Leaf | Branch

export const isBranch = (n: RuleNode): n is Branch =>
  typeof n === 'object' && n !== null && 'op' in n

/** Depth-first count of leaves, used for the "an empty group matches all" guard. */
function leafCount(n: RuleNode | null): number {
  if (!n) return 0
  return isBranch(n) ? n.children.reduce((a, c) => a + leafCount(c), 0) : 1
}

/** True when any AND/OR node has no children — the whole-scope hole. */
export function hasEmptyGroup(n: RuleNode | null): boolean {
  if (!n || !isBranch(n)) return false
  if (n.children.length === 0) return true
  return n.children.some(hasEmptyGroup)
}

function defaultValueFor(spec: FieldSpec, operator: string): unknown {
  if (operator === 'in') return spec.values.length ? [spec.values[0]] : []
  if (operator === 'between') return [0, 100]
  if (spec.kind === 'BOOLEAN') return true
  if (spec.kind === 'ENUM' || spec.kind === 'TAG') return spec.values[0] ?? ''
  if (spec.kind === 'SHORT') return 0
  return ''
}

function LeafRow({ leaf, specs, onChange, onRemove }: {
  leaf: Leaf
  specs: FieldSpec[]
  onChange: (next: Leaf) => void
  onRemove: () => void
}) {
  const spec = specs.find((s) => s.field === leaf.field)
  const ops = spec?.operators ?? ['eq']

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5">
      <select
        aria-label="Field"
        className="erp-input h-7 text-erp-xs"
        value={leaf.field}
        onChange={(e) => {
          const next = specs.find((s) => s.field === e.target.value)
          if (!next) return
          const op = next.operators.includes(leaf.operator) ? leaf.operator : next.operators[0]!
          onChange({ field: next.field, operator: op, value: defaultValueFor(next, op) })
        }}
      >
        {specs.map((s) => (
          <option key={s.field} value={s.field}>{fieldLabel(s.field)}</option>
        ))}
      </select>

      <select
        aria-label="Operator"
        className="erp-input h-7 text-erp-xs"
        value={leaf.operator}
        onChange={(e) => {
          const op = e.target.value
          onChange({ ...leaf, operator: op, value: spec ? defaultValueFor(spec, op) : leaf.value })
        }}
      >
        {ops.map((o) => <option key={o} value={o}>{operatorLabel(o)}</option>)}
      </select>

      <LeafValue leaf={leaf} spec={spec} onChange={onChange} />

      <button
        type="button"
        className="erp-btn-ghost h-7 w-7 p-0 ml-auto"
        onClick={onRemove}
        aria-label={`Remove condition on ${fieldLabel(leaf.field)}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

function LeafValue({ leaf, spec, onChange }: {
  leaf: Leaf; spec?: FieldSpec; onChange: (next: Leaf) => void
}) {
  if (!spec) return <span className="text-erp-xs text-muted-foreground">unknown field</span>

  if (leaf.operator === 'between') {
    const v = Array.isArray(leaf.value) ? leaf.value as number[] : [0, 100]
    return (
      <span className="flex items-center gap-1">
        <input aria-label="From" type="number" className="erp-input h-7 w-16 text-erp-xs"
          value={v[0] ?? 0}
          onChange={(e) => onChange({ ...leaf, value: [Number(e.target.value), v[1] ?? 100] })} />
        <span className="text-erp-xs text-muted-foreground">and</span>
        <input aria-label="To" type="number" className="erp-input h-7 w-16 text-erp-xs"
          value={v[1] ?? 100}
          onChange={(e) => onChange({ ...leaf, value: [v[0] ?? 0, Number(e.target.value)] })} />
      </span>
    )
  }

  if (leaf.operator === 'in') {
    const arr = Array.isArray(leaf.value) ? leaf.value as string[] : []
    // A TAG field has no server-supplied value list — tags are campaign data,
    // not a vocabulary — so it takes free text.
    if (spec.kind === 'TAG' || spec.values.length === 0) {
      return (
        <input
          aria-label="Values" className="erp-input h-7 w-48 text-erp-xs"
          placeholder="comma-separated"
          value={arr.join(', ')}
          onChange={(e) => onChange({
            ...leaf,
            value: e.target.value.split(',').map((x) => x.trim().toUpperCase()).filter(Boolean),
          })}
        />
      )
    }
    return (
      <span className="flex flex-wrap gap-1">
        {spec.values.map((v) => {
          const on = arr.includes(v)
          return (
            <button
              key={v} type="button"
              aria-pressed={on}
              className={cn('rounded border px-1.5 py-0.5 text-erp-xs',
                on ? 'bg-chocolate-soft/60 text-chocolate border-chocolate-muted'
                   : 'bg-background text-muted-foreground border-border')}
              onClick={() => onChange({
                ...leaf, value: on ? arr.filter((x) => x !== v) : [...arr, v],
              })}
            >
              {valueLabel(v)}
            </button>
          )
        })}
      </span>
    )
  }

  if (spec.kind === 'BOOLEAN') {
    return (
      <select aria-label="Value" className="erp-input h-7 text-erp-xs"
        value={String(leaf.value)}
        onChange={(e) => onChange({ ...leaf, value: e.target.value === 'true' })}>
        <option value="true">yes</option>
        <option value="false">no</option>
      </select>
    )
  }

  if (spec.kind === 'SHORT') {
    return (
      <input aria-label="Value" type="number" className="erp-input h-7 w-20 text-erp-xs"
        value={Number(leaf.value ?? 0)}
        onChange={(e) => onChange({ ...leaf, value: Number(e.target.value) })} />
    )
  }

  if (spec.values.length) {
    return (
      <select aria-label="Value" className="erp-input h-7 text-erp-xs"
        value={String(leaf.value ?? '')}
        onChange={(e) => onChange({ ...leaf, value: e.target.value })}>
        {spec.values.map((v) => <option key={v} value={v}>{valueLabel(v)}</option>)}
      </select>
    )
  }

  return (
    <input aria-label="Value" className="erp-input h-7 w-40 text-erp-xs"
      value={String(leaf.value ?? '')}
      onChange={(e) => onChange({ ...leaf, value: e.target.value })} />
  )
}

function NodeEditor({ node, specs, onChange, onRemove, depth = 0 }: {
  node: RuleNode
  specs: FieldSpec[]
  onChange: (next: RuleNode) => void
  onRemove?: () => void
  depth?: number
}) {
  if (!isBranch(node)) {
    return <LeafRow leaf={node} specs={specs} onChange={onChange} onRemove={onRemove ?? (() => {})} />
  }

  const empty = node.children.length === 0

  return (
    <div className={cn('rounded-md border p-2 space-y-2',
      empty ? 'border-red-300 dark:border-red-900 bg-red-50/40 dark:bg-red-950/20' : 'border-border bg-muted/20')}>
      <div className="flex items-center gap-2">
        <select
          aria-label="Group operator"
          className="erp-input h-7 text-erp-xs"
          value={node.op}
          onChange={(e) => onChange({ ...node, op: e.target.value as 'AND' | 'OR' })}
        >
          <option value="AND">all of</option>
          <option value="OR">any of</option>
        </select>
        <button
          type="button" className="erp-btn-ghost h-7 text-erp-xs"
          onClick={() => onChange({
            ...node,
            children: [...node.children, { field: specs[0]?.field ?? '', operator: specs[0]?.operators[0] ?? 'eq',
              value: specs[0] ? defaultValueFor(specs[0], specs[0].operators[0] ?? 'eq') : '' }],
          })}
        >
          <Plus className="h-3.5 w-3.5" /> Condition
        </button>
        {depth < 2 && (
          <button
            type="button" className="erp-btn-ghost h-7 text-erp-xs"
            onClick={() => onChange({ ...node, children: [...node.children, { op: 'OR', children: [] }] })}
          >
            <Plus className="h-3.5 w-3.5" /> Group
          </button>
        )}
        {onRemove && (
          <button type="button" className="erp-btn-ghost h-7 w-7 p-0 ml-auto"
            onClick={onRemove} aria-label="Remove group">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {empty ? (
        // The guard, stated where the mistake is made rather than in a toast
        // after a failed request.
        <p className="px-1 text-erp-xs text-red-700 dark:text-red-300">
          An empty group matches <strong>everybody</strong> in scope. Add a condition or remove the group.
        </p>
      ) : (
        <div className="space-y-2 pl-2 border-l-2 border-border">
          {node.children.map((c, i) => (
            <NodeEditor
              // Keyed by CONTENT, not by index. Removing the second of three
              // conditions with an index key hands child 3's state to the slot
              // child 2 vacated, so the row the operator deleted appears to stay
              // and the one below it appears to change.
              key={`${i}-${JSON.stringify(c)}`}
              node={c}
              specs={specs}
              depth={depth + 1}
              onChange={(next) => onChange({
                ...node, children: node.children.map((x, k) => (k === i ? next : x)),
              })}
              onRemove={() => onChange({
                ...node, children: node.children.filter((_, k) => k !== i),
              })}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function RuleBuilder({
  value, onChange, groupKeys, onGroupKeysChange, scopeUnitId, readOnly,
}: {
  value: RuleNode | null
  onChange: (next: RuleNode | null) => void
  groupKeys: string[]
  onGroupKeysChange: (next: string[]) => void
  scopeUnitId?: string | null
  readOnly?: boolean
}) {
  const [specs, setSpecs] = useState<FieldSpec[] | null>(null)
  const [groups, setGroups] = useState<TargetGroupOption[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewing, setPreviewing] = useState(false)

  useEffect(() => {
    let alive = true
    Promise.all([targetingFields(), targetingGroups(scopeUnitId)])
      .then(([f, g]) => { if (alive) { setSpecs(f); setGroups(g) } })
      .catch((e) => { if (alive) setLoadError(explain(e)) })
    return () => { alive = false }
  }, [scopeUnitId])

  const empty = hasEmptyGroup(value)
  const leaves = leafCount(value)

  // Debounced preview. The count has to move as the operator types, which is
  // why it is a count and never a materialisation.
  useEffect(() => {
    if (empty) { setPreview(null); return }
    if (!value && groupKeys.length === 0) { setPreview(null); return }
    let alive = true
    setPreviewing(true)
    const t = setTimeout(() => {
      // Presets are part of the definition, so they have to be part of the
      // preview. Skipping them meant a rule built from a preset alone rendered
      // "0 people in scope match this definition" — a null shown as a zero, in
      // the one place where somebody decides whether a rule is worth publishing.
      // `/targeting/compose` is the server's own combiner; composing the two
      // here would be a second copy of the grammar.
      const definition: Promise<unknown> = groupKeys.length > 0
        ? composeDefinition(groupKeys, value ?? undefined)
        : Promise.resolve(value)
      definition
        .then((def) => previewTargeting(def, scopeUnitId))
        .then((r) => { if (alive) { setPreview(r); setPreviewError(null) } })
        .catch((e) => { if (alive) { setPreview(null); setPreviewError(explain(e)) } })
        .finally(() => { if (alive) setPreviewing(false) })
    }, 350)
    return () => { alive = false; clearTimeout(t) }
  }, [JSON.stringify(value), scopeUnitId, empty, groupKeys.join(',')])

  const selectedGroups = useMemo(
    () => (groups ?? []).filter((g) => groupKeys.includes(g.key)),
    [groups, groupKeys],
  )

  if (loadError) return <ErrorStrip message={loadError} />
  if (!specs || !groups) return <LoadingBlock label="Loading the targeting grammar…" />

  return (
    <div className="space-y-4">
      {/* ── presets ── */}
      <section className="space-y-2">
        <h4 className="text-erp-sm font-medium">Target groups</h4>
        <p className="text-erp-xs text-muted-foreground">
          Selecting two groups <strong>widens</strong> the pool — a person in either one matches.
          Conditions below then narrow it.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {groups.map((g) => {
            const on = groupKeys.includes(g.key)
            const missingTags = g.requiresTag.filter((t) => (g.tagCoverage[t] ?? 0) === 0)
            const unusable = g.requiresTag.length > 0 && missingTags.length === g.requiresTag.length
            return (
              <button
                key={g.key}
                type="button"
                disabled={readOnly}
                aria-pressed={on}
                title={g.requiresTag.length
                  ? g.requiresTag.map((t) => `${t}: ${fmtNumber(g.tagCoverage[t] ?? 0)} voters tagged`).join(' · ')
                  : undefined}
                className={cn(
                  'rounded-md border px-2 py-1 text-erp-xs text-left transition-colors',
                  on ? 'bg-chocolate-soft/60 text-chocolate border-chocolate-muted'
                     : 'bg-background border-border hover:bg-muted',
                  unusable && 'opacity-70',
                )}
                onClick={() => onGroupKeysChange(
                  on ? groupKeys.filter((k) => k !== g.key) : [...groupKeys, g.key],
                )}
              >
                <span className="font-medium">{g.displayName}</span>
                <span className="ml-1.5 tabular-nums text-muted-foreground">
                  {fmtNumber(g.matchingCount)}
                </span>
                {/* The honesty line. A preset cannot conjure data the campaign
                    has not captured, and a bare 0 hides which of the two it is. */}
                {unusable && (
                  <span className="block text-[10px] leading-tight text-amber-700 dark:text-amber-300">
                    needs tag {missingTags.join(' / ')} — nobody tagged yet
                  </span>
                )}
              </button>
            )
          })}
        </div>
        {selectedGroups.length > 1 && (
          <Note tone="info">
            {selectedGroups.length} groups selected — a person matching <em>any</em> of them is in scope.
          </Note>
        )}
      </section>

      {/* ── conditions ── */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-erp-sm font-medium">Conditions</h4>
          {!value && !readOnly && (
            <button
              type="button" className="erp-btn-ghost h-7 text-erp-xs"
              onClick={() => onChange({ op: 'AND', children: [] })}
            >
              <Plus className="h-3.5 w-3.5" /> Add conditions
            </button>
          )}
        </div>
        {value ? (
          <NodeEditor
            node={value}
            specs={specs}
            onChange={(n) => onChange(n)}
            onRemove={() => onChange(null)}
          />
        ) : (
          <p className="text-erp-xs text-muted-foreground">
            No extra conditions. {groupKeys.length
              ? 'The selected groups define the pool on their own.'
              : 'Select a group above, or add a condition.'}
          </p>
        )}
        <p className="text-erp-xs text-muted-foreground">
          {leaves} condition{leaves === 1 ? '' : 's'}. Fields, operators and values come from the
          register’s own segment grammar — this screen holds no copy of it.
        </p>
      </section>

      {/* ── preview ── */}
      <section className="rounded-md border border-border bg-muted/20 p-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" aria-hidden />
          <h4 className="text-erp-sm font-medium">Matching now</h4>
        </div>
        {empty ? (
          <p className="mt-2 text-erp-xs text-red-700 dark:text-red-300">
            Not previewed: an empty group would match everybody in scope.
          </p>
        ) : previewError ? (
          <ErrorStrip message={previewError} className="mt-2" />
        ) : !value && groupKeys.length === 0 ? (
          <p className="mt-2 text-erp-xs text-muted-foreground">
            Select a group or add a condition to see how many people match.
          </p>
        ) : (
          <>
            <p className="mt-1 text-erp-2xl font-semibold tabular-nums" data-testid="preview-count">
              {/* `?? 0` here would say "nobody matches" for a definition that has
                  not been counted yet. An em dash says the count is missing. */}
              {previewing && !preview ? '…' : preview ? fmtNumber(preview.count) : '—'}
            </p>
            <p className="text-erp-xs text-muted-foreground">
              people in scope match this definition{previewing ? ' (updating…)' : ''}.
            </p>
            {preview && preview.sample.length > 0 && (
              <ul className="mt-2 space-y-0.5">
                {preview.sample.slice(0, 5).map((v) => (
                  <li key={v.id} className="text-erp-xs text-muted-foreground">
                    {v.displayName ?? '—'}
                    <span className="ml-2 opacity-70">support {v.supportLevel}</span>
                  </li>
                ))}
                <li className="text-erp-xs text-muted-foreground italic">
                  A sample. No contact detail is shown here, and none is available from this endpoint.
                </li>
              </ul>
            )}
          </>
        )}
      </section>
    </div>
  )
}
